"use client";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import TextComponent from "@/app/_components/TextComponent";
import cn from "@/utils/cn";
import { useZStore } from "@/utils/z-store";
import { useSession } from "next-auth/react";
import axios from "axios";

const UnderConstructionBanner = ({ className }: { className?: string }) => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const { data: session } = useSession();
  const { data, actions } = useZStore();
  const [allowedEmails, setAllowedEmails] = useState<string[]>([]);

  useEffect(() => {
    const fetchAllowedEmails = async () => {
      try {
        const response = await axios.get("/api/allowed-emails");
        setAllowedEmails(response.data);
      } catch (error) {
        console.error("Failed to fetch allowed emails:", error);
      }
    };

    fetchAllowedEmails();
  }, []);

  const userHasBypassAccess =
    session?.user?.email && allowedEmails.includes(session.user.email);

  useEffect(() => {
    if (userHasBypassAccess) {
      actions.setBannerVisible(false);
    }
  }, [userHasBypassAccess, actions]);

  // Define routes where the banner should be shown
  const allowedBannerRoutes = [
    "/dashboard/home",
    "/dashboard/reports",
    "/dashboard/edit",
    "/dashboard/report-builder",
  ];

  // Check if current pathname matches any of the allowed routes AND banner is enabled in store
  const shouldShowBanner =
    allowedBannerRoutes.includes(pathname) && (data.isBannerVisible ?? true);

  // Always call useEffect - but conditionally execute its logic
  useEffect(() => {
    // Only execute the effect logic if banner should be shown
    if (!shouldShowBanner) {
      return;
    }

    const findScrollableParent = (
      element: HTMLElement | null,
    ): HTMLElement | null => {
      if (!element) return null;
      let parent = element.parentElement;
      while (parent) {
        const { overflowY } = window.getComputedStyle(parent);
        if (overflowY === "auto" || overflowY === "scroll") {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const scrollableParent = findScrollableParent(bannerRef.current);
    const body = document.body;

    const originalBodyOverflow = body.style.overflow;
    let originalParentOverflow: string | null = null;

    body.style.overflow = "hidden";
    if (scrollableParent) {
      originalParentOverflow = scrollableParent.style.overflow;
      scrollableParent.style.overflow = "hidden";
    }

    return () => {
      body.style.overflow = originalBodyOverflow || "auto";
      if (scrollableParent) {
        scrollableParent.style.overflow = originalParentOverflow || "auto";
      }
    };
  }, [shouldShowBanner]); // Add shouldShowBanner as dependency

  // Return null AFTER all hooks have been called
  if (!shouldShowBanner) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className={cn(
        "absolute justify-center items-center flex inset-0  bg-[#ECECECB2]",
        className,
      )}
    >
      <TextComponent type="h2" className="text-center text-kd-lte-blue">
        ⚠️ Website Under Construction
      </TextComponent>
    </div>
  );
};

export default UnderConstructionBanner;
